import { z } from 'zod';
import { healthEnvironmentSchema } from './health.js';
/**
 * 0.17.0: Trace Admin's Keys page (GET /v1/admin/keys and its two changes). platform-api shows the NAMES of the secrets
 * in its own environment's Infisical folders of the Trace platform family, with Infisical's own metadata (when each
 * last changed, its version), and joins what staff recorded about each key (its expiry, a reminder, a chosen provider).
 * platform-api holds no Infisical credential: a GitHub Actions job (keys-sync.yml) lists the names with the deploy's
 * own Infisical identity and pushes them (POST /v1/keys-sync/inventory, authenticated by the job's GitHub OIDC token).
 * No secret value is ever requested, stored, logged or returned: nothing in these shapes can carry one.
 *
 * Each Trace Admin shows its own environment only (the same rule as Health): production for TRACE_ENV=production, dev
 * otherwise.
 */
/**
 * The Infisical projects the inventory reads, every folder of each (listed recursively), for the environment of the
 * answering platform-api. A fixed allow-list: the sync route refuses any other project. Only the trace project now:
 * the Trace Enterprise project (4beb0891-...) is deprecated and no longer read (migration 0021 removed its rows). The
 * inventory stays keyed by project, so adding one back is a change to this list, the database vocabulary and the job.
 */
export const KEY_PROJECTS = [
    { id: 'dde8b9e3-d8dd-42b1-969b-c08861cba4ff', slug: 'trace-fb-ae', name: 'Trace' },
];
export const KEY_PROJECT_IDS = ['dde8b9e3-d8dd-42b1-969b-c08861cba4ff'];
export const keyProjectSchema = z.enum(KEY_PROJECT_IDS);
/** The project's name as staff know it (Trace). */
export function keyProjectName(project) {
    return KEY_PROJECTS.find((entry) => entry.id === project).name;
}
/**
 * An Infisical folder path: / or /a/b, segments of letters, digits, dashes and underscores (Infisical's own folder
 * name rule), at most 8 deep and 256 characters.
 */
export const keyFolderSchema = z
    .string()
    .max(256)
    .regex(/^\/(?:[A-Za-z0-9_-]{1,64}(?:\/[A-Za-z0-9_-]{1,64}){0,7})?$/, 'an Infisical folder such as /backend');
/** A secret's name as Infisical holds it (upper case, digits and underscores). Never a value. */
export const keyNameSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{0,127}$/, 'a secret name such as RESEND_API_KEY');
/**
 * What a key is, from its name: a token a provider issued (it may expire at the provider), a secret Trace signs or
 * verifies with, or a key Trace encrypts data with (rotating it means re-encrypting data, so the page never nudges it).
 */
export const KEY_KINDS = ['provider_token', 'signing_secret', 'encryption_key'];
export const keyKindSchema = z.enum(KEY_KINDS);
/**
 * Who issued a key: a provider (its logo on the page, or a plain globe when it has none), Trace itself, or other (a
 * provider staff named in their own words, providerName).
 */
export const KEY_PROVIDERS = [
    'digitalocean',
    'digitalocean_spaces',
    'stripe',
    'resend',
    'auth0',
    'anthropic',
    'openai',
    'google_maps',
    'mapbox',
    'twilio',
    'sentry',
    'ngrok',
    'postgresql',
    'agentmail',
    'reducto',
    'exchangerate_api',
    'jev',
    'trace',
    'other',
];
export const keyProviderSchema = z.enum(KEY_PROVIDERS);
/**
 * A key's state today, most urgent first: past its expiry, within its reminder window, a provider token whose expiry is
 * not known yet, an expiry ahead of the window, or a key that does not expire.
 */
export const KEY_STATUSES = ['expired', 'due', 'not_set', 'ok', 'no_expiry'];
export const keyStatusSchema = z.enum(KEY_STATUSES);
/**
 * Where a key's expiry comes from. None of the providers behind Trace's keys exposes a token's expiry through its API
 * (checked 2026-10-12: DigitalOcean, Stripe, Resend, Auth0, Spaces), so an expiry is one staff entered ("set by you"),
 * or staff said the key does not expire. A key nobody recorded anything for has none (null).
 */
export const KEY_EXPIRY_SOURCES = ['set_by_you', 'does_not_expire'];
export const keyExpirySourceSchema = z.enum(KEY_EXPIRY_SOURCES);
/** Where the inventory came from: Infisical (the latest snapshot the sync job pushed), or the local fake. */
export const KEY_SOURCES = ['infisical', 'fake'];
export const keySourceSchema = z.enum(KEY_SOURCES);
/** Why Keys shows nothing: the sync job has not pushed an inventory for this environment yet. */
export const KEY_NOT_CONNECTED_REASONS = ['not_synced'];
export const keyNotConnectedReasonSchema = z.enum(KEY_NOT_CONNECTED_REASONS);
/** The reminder windows the page offers, in days before the expiry; the default is 30. */
export const KEY_REMINDER_DAYS = [7, 14, 30, 60, 90];
export const KEY_DEFAULT_REMINDER_DAYS = 30;
export const keyReminderDaysSchema = z.number().int().min(1).max(365);
/**
 * Whether an entry is a secret to track, from its name: a secret (a token, a password, a key), or configuration that
 * happens to sit beside the secrets (an address, a port, a model, a flag, a public build value), which the page leaves
 * off unless staff track it.
 */
export const KEY_CATEGORIES = ['secret', 'config'];
export const keyCategorySchema = z.enum(KEY_CATEGORIES);
/** One row of the page: a key (or a key id and its secret collapsed into one row), never its value. */
export const adminKeySchema = z.object({
    /** The secret's name; with the project, what a staff change is recorded under. */
    name: keyNameSchema,
    project: keyProjectSchema,
    folder: keyFolderSchema,
    /** The identifier collapsed into this row (EVIDENCE_READER_ACCESS_KEY_ID beside its secret), or null. */
    pairedName: keyNameSchema.nullable(),
    kind: keyKindSchema,
    /** The provider: the one staff chose for this name, else the one the name's rules give, else null (unmatched). */
    provider: keyProviderSchema.nullable(),
    /** True when staff chose the provider (Choose provider), false when it comes from the name. */
    providerChosen: z.boolean(),
    /** The provider in staff's words when it is other, else null. */
    providerName: z.string().nullable(),
    /** Secret or configuration, by the name's rules. */
    category: keyCategorySchema,
    /** Shown and counted (the name's rules, or staff's Track this / Not a secret); untracked rows are the Hidden tab. */
    tracked: z.boolean(),
    /** True when staff chose tracked against the rules. */
    trackingChosen: z.boolean(),
    /** What uses the key, in the words staff know (Billing, Brokers). */
    usedBy: z.string(),
    /** Infisical's own time of the last change of the secret (the later of a pair). */
    lastChangedAt: z.iso.datetime(),
    /** Infisical's version of the secret. */
    version: z.number().int().min(0),
    /** The day the key expires (UTC), or null. */
    expiresOn: z.iso.date().nullable(),
    expirySource: keyExpirySourceSchema.nullable(),
    /** Days before the expiry the key turns due; null: no reminder. */
    reminderDays: keyReminderDaysSchema.nullable(),
    /** Days from today (UTC) to the expiry: negative once expired, null without one. */
    daysLeft: z.number().int().nullable(),
    status: keyStatusSchema,
    /** When staff last changed what is recorded for this key, and who; null when nothing is. */
    settingsUpdatedAt: z.iso.datetime().nullable(),
    settingsUpdatedBy: z.string().nullable(),
});
/**
 * A project or folder Infisical could not list at the last sync, in plain words (never a secret, never a response
 * body). folder / means the whole project. Its keys stay as of the previous sync that read them.
 */
export const keyFailureSchema = z.object({
    project: keyProjectSchema,
    folder: keyFolderSchema,
    message: z.string(),
});
/** The counts behind the tabs, the summary strip and the navigation badge (expired plus due). */
export const keyCountsSchema = z.object({
    expired: z.number().int().min(0),
    due: z.number().int().min(0),
    notSet: z.number().int().min(0),
    ok: z.number().int().min(0),
    noExpiry: z.number().int().min(0),
    tracked: z.number().int().min(0),
    /** Entries left off the page (configuration, or Not a secret). */
    hidden: z.number().int().min(0),
});
/**
 * GET /v1/admin/keys: this environment's keys, most urgent first. connected false (with the reason) until the sync job
 * pushed a first inventory: no keys at all then. A folder the job could not list is named in failures while the rest
 * is answered; stale is true once the latest sync is older than KEYS_SYNC_STALE_HOURS.
 */
export const adminKeysSchema = z.object({
    environment: healthEnvironmentSchema,
    source: keySourceSchema,
    connected: z.boolean(),
    notConnectedReason: keyNotConnectedReasonSchema.nullable(),
    /** When the inventory shown was read from Infisical (the latest sync), or null before the first one. */
    syncedAt: z.iso.datetime().nullable(),
    /** The latest sync is older than KEYS_SYNC_STALE_HOURS (the job runs hourly and after every deploy). */
    stale: z.boolean(),
    /** Today (UTC) as the statuses were worked out. */
    today: z.iso.date(),
    /** Infisical's web app, for "Open Infisical"; null when not connected. */
    consoleUrl: z.string().nullable(),
    failures: z.array(keyFailureSchema),
    counts: keyCountsSchema,
    keys: z.array(adminKeySchema),
});
/**
 * GET /v1/admin/keys/summary: the counts alone, for the navigation badge and Health's line. No key name, so a page
 * that only shows a badge does not read the inventory's names.
 */
export const adminKeysSummarySchema = z.object({
    environment: healthEnvironmentSchema,
    connected: z.boolean(),
    counts: keyCountsSchema,
});
/**
 * PUT /v1/admin/keys/{name}/expiry: the key expires on a day (with an optional reminder, null for none), or it does
 * not expire. Staff with staff.orgs.manage (platform admins); audited.
 */
export const keyExpiryChangeSchema = z.discriminatedUnion('expiry', [
    z.object({
        expiry: z.literal('date'),
        expiresOn: z.iso.date(),
        reminderDays: keyReminderDaysSchema.nullable(),
    }),
    z.object({ expiry: z.literal('never') }),
]);
/**
 * PUT /v1/admin/keys/{name}/provider: the provider staff chose for this key name, or null to go back to the name's
 * rules. Staff with staff.orgs.manage (platform admins); audited.
 */
export const keyProviderChangeSchema = z
    .object({
    provider: keyProviderSchema.nullable(),
    /** The provider's name in staff's words, required with other and only with it. */
    name: z.string().trim().min(1).max(60).optional(),
})
    .refine((change) => (change.provider === 'other') === (change.name !== undefined), {
    message: 'Name the provider when it is other, and only then.',
    path: ['name'],
});
/**
 * PUT /v1/admin/keys/{project}/{name}/tracking: whether the entry is a secret to track (true: Track this), not one
 * (false: Not a secret), or null to go back to the name's rules. Staff with staff.orgs.manage; audited.
 */
export const keyTrackingChangeSchema = z.object({ tracked: z.boolean().nullable() });
/** How old the latest sync may be before the page says so quietly (the job runs every hour and after each deploy). */
export const KEYS_SYNC_STALE_HOURS = 3;
/**
 * The audience of the GitHub OIDC token the sync job presents. platform-api accepts it only from GitHub's issuer, for
 * this repository's keys-sync workflow on dev or main, in the GitHub environment matching its own environment, once.
 */
export const KEYS_SYNC_AUDIENCE = 'urn:trace:keys-sync';
/** Routes of the Keys sync (not staff, not internal service tokens: a GitHub Actions OIDC token). */
export const PLATFORM_KEYS_SYNC_API = {
    inventory: '/v1/keys-sync/inventory',
};
/** One secret of the pushed inventory: its project, folder, name and Infisical's metadata, and nothing else (strict). */
export const keysSyncSecretSchema = z
    .object({
    project: keyProjectSchema,
    folder: keyFolderSchema,
    name: keyNameSchema,
    updatedAt: z.iso.datetime({ offset: true }),
    version: z.number().int().min(0),
})
    .strict();
/**
 * POST /v1/keys-sync/inventory: the whole inventory of one environment, which replaces the previous one. Strict at
 * every level: a field that is not listed here (a value, a comment, a tag) refuses the whole request (400), so nothing
 * of a value can ever be stored. failures names the projects (folder /) or folders the job could not list: their
 * previous keys stay.
 */
export const keysSyncRequestSchema = z
    .object({
    environment: healthEnvironmentSchema,
    secrets: z.array(keysSyncSecretSchema).max(2000),
    failures: z
        .array(z
        .object({
        project: keyProjectSchema,
        folder: keyFolderSchema,
        message: z.string().trim().min(1).max(300),
    })
        .strict())
        .max(100),
})
    .strict();
export const keysSyncResponseSchema = z.object({
    environment: healthEnvironmentSchema,
    syncedAt: z.iso.datetime(),
    secrets: z.number().int().min(0),
    failures: z.number().int().min(0),
});
//# sourceMappingURL=keys.js.map